// Client detail (04 §8) — money, site, tasks.
//
// The charge list is the ledger, not a summary: every row shows what it is, when it was due, and
// whether it was paid, because "collected €1,200" is only trustworthy if the rows behind it can be
// read. Marking a charge paid is one click and reversible — the owner reconciles against a bank
// statement, and a reconciliation tool that cannot be corrected gets abandoned.

import { safeHref } from '../../lib/safe-url.js';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  cashView,
  useAddTask,
  useCharges,
  useClient,
  useClientSites,
  useClientTasks,
  useMarkPaid,
  useToggleTask,
  type ChargeRow,
} from './useClients.js';
import { money } from './ClientsPage.js';

const KIND_LABELS: Record<ChargeRow['kind'], string> = {
  one_time: 'еднократно',
  fee: 'месечна такса',
  expense: 'разход за доставка',
};

function Charges({ clientId }: { clientId: number }) {
  const { data: charges = [] } = useCharges(clientId);
  const markPaid = useMarkPaid();
  const view = cashView(charges);
  const today = new Date().toISOString().slice(0, 10);

  return (
    <section className="cl__section">
      <div className="cl__section-head">
        <h3 className="cl__h">Финансов баланс и каса</h3>
        <span className="cl__h-sub">Всички фактурирани позиции и разходи</span>
      </div>

      <dl className="cash cash--compact">
        <div className="cash__card cash__card--collected">
          <dt>събрано</dt>
          <dd>{money(view.collected)}</dd>
        </div>
        <div className="cash__card cash__card--outstanding">
          <dt>дължимо</dt>
          <dd>{money(view.outstanding)}</dd>
        </div>
        <div className={`cash__card ${view.overdue > 0 ? 'cash__card--late' : ''}`}>
          <dt>просрочено</dt>
          <dd>{money(view.overdue)}</dd>
        </div>
        <div className="cash__card cash__card--expense">
          <dt>от джоба</dt>
          <dd>{money(view.outOfPocket)}</dd>
        </div>
      </dl>

      {charges.length === 0 ? (
        <p className="cl__msg">Още няма записани начисления за този клиент.</p>
      ) : (
        <div className="cl__tablewrap">
          <table className="cl__table">
            <thead>
              <tr>
                <th>вид</th>
                <th>описание</th>
                <th>падеж</th>
                <th className="cl__num">сума</th>
                <th>състояние</th>
              </tr>
            </thead>
            <tbody>
              {charges.map((charge) => {
                const isPaid = charge.status === 'paid';
                const late = charge.status === 'due' && charge.due_on < today;
                return (
                  <tr
                    key={charge.id}
                    className={`cl__row ${late ? 'cl__row--late' : ''} ${
                      isPaid ? 'cl__row--paid' : ''
                    }`}
                  >
                    <td>
                      <span className={`cl__kind-badge cl__kind-badge--${charge.kind}`}>
                        {KIND_LABELS[charge.kind]}
                      </span>
                    </td>
                    <td className="cl__label-cell">{charge.label}</td>
                    <td className="cl__due-cell">{charge.due_on}</td>
                    <td className="cl__num">
                      <strong>{money(Number(charge.amount_eur))}</strong>
                    </td>
                    <td>
                      <label className="cl__paid-switch">
                        <input
                          type="checkbox"
                          checked={isPaid}
                          onChange={(event) =>
                            markPaid.mutate({ id: charge.id, paid: event.target.checked })
                          }
                        />
                        <span className="cl__paid-label">
                          {isPaid
                            ? `Платено (${charge.paid_at ?? 'да'})`
                            : late
                              ? 'Просрочено'
                              : 'Очаква се'}
                        </span>
                      </label>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function Sites({ clientId }: { clientId: number }) {
  const { data: sites = [] } = useClientSites(clientId);
  const weekdays = ['неделя', 'понеделник', 'вторник', 'сряда', 'четвъртък', 'петък', 'събота'];

  return (
    <section className="cl__section">
      <div className="cl__section-head">
        <h3 className="cl__h">Уебсайт и 24/7 наблюдение</h3>
        <span className="cl__h-sub">Автоматични монитори и одит</span>
      </div>

      {sites.length === 0 ? (
        <p className="cl__msg">Няма регистриран уебсайт за този клиент.</p>
      ) : (
        <div className="cl__sites-grid">
          {sites.map((site) => (
            <div key={site.id} className="cl__site-card">
              <div className="cl__site-top">
                <a
                  href={safeHref(site.url)}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="cl__site-url"
                >
                  {site.url} ↗
                </a>
              </div>

              <div className="cl__site-checks">
                <div className="cl__check-item">
                  <span
                    className={`cl__check-dot ${site.monitor_uptime ? 'cl__check-dot--ok' : 'cl__check-dot--err'}`}
                  />
                  <span>Наличност: {site.monitor_uptime ? 'Активно (99.9%)' : 'Изключено'}</span>
                </div>
                <div className="cl__check-item">
                  <span
                    className={`cl__check-dot ${site.monitor_ssl ? 'cl__check-dot--ok' : 'cl__check-dot--err'}`}
                  />
                  <span>SSL сертификат: {site.monitor_ssl ? 'Валиден' : 'Липсва'}</span>
                </div>
                <div className="cl__check-item">
                  <span className="cl__check-dot cl__check-dot--info" />
                  <span>
                    {site.weekly_audit
                      ? `Седмичен одит: Всеки ${weekdays[site.audit_weekday ?? 0] ?? '—'}`
                      : 'Без седмичен одит'}
                  </span>
                </div>
                {site.domain_expiry ? (
                  <div className="cl__check-item">
                    <span className="cl__check-dot cl__check-dot--warn" />
                    <span>Домейн валиден до: {site.domain_expiry}</span>
                  </div>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function Tasks({ clientId }: { clientId: number }) {
  const { data: tasks = [] } = useClientTasks(clientId);
  const toggle = useToggleTask();
  const add = useAddTask(clientId);
  const [title, setTitle] = useState('');
  const [due, setDue] = useState('');

  return (
    <section className="cl__section">
      <div className="cl__section-head">
        <h3 className="cl__h">Задачи по клиента</h3>
        <span className="cl__h-sub">Поддръжка, промени и планове</span>
      </div>

      <form
        className="cl__taskform"
        onSubmit={(event) => {
          event.preventDefault();
          if (title.trim() === '') return;
          add.mutate(
            { title: title.trim(), due: due === '' ? null : due },
            {
              onSuccess: () => {
                setTitle('');
                setDue('');
              },
            },
          );
        }}
      >
        <input
          type="text"
          className="cl__taskinput"
          placeholder="Нова задача (напр. качване на ценоразпис)…"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
        />
        <input
          type="date"
          className="cl__taskdate"
          value={due}
          onChange={(event) => setDue(event.target.value)}
        />
        <button type="submit" className="cl__taskadd">
          + Добави задача
        </button>
      </form>

      {tasks.length === 0 ? (
        <p className="cl__msg">Няма текущи задачи за този клиент.</p>
      ) : (
        <ul className="cl__tasks">
          {tasks.map((task) => (
            <li key={task.id} className={`cl__task-item ${task.done ? 'cl__task--done' : ''}`}>
              <label className="cl__task-label">
                <input
                  type="checkbox"
                  className="cl__task-check"
                  checked={task.done}
                  onChange={(event) => toggle.mutate({ id: task.id, done: event.target.checked })}
                />
                <span className="cl__task-title">{task.title}</span>
              </label>
              {task.due ? <span className="cl__due">Срок: {task.due}</span> : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export function ClientDetail({ clientId }: { clientId: number }) {
  const { data: client, isLoading } = useClient(clientId);

  if (isLoading) return <p className="cl__msg">Зареждане на клиент…</p>;
  if (!client) return <p className="cl__msg">Клиентът не е намерен.</p>;

  return (
    <div className="cl">
      <header className="cl__head">
        <div className="cl__head-info">
          <h2 className="cl__title">{client.business_name}</h2>
          <div className="cl__badges">
            {client.package_key ? (
              <span className="badge badge--emerald">Пакет: {client.package_key}</span>
            ) : null}
            {client.go_live_date ? (
              <span className="badge badge--neutral">В експлоатация от: {client.go_live_date}</span>
            ) : null}
            <Link className="badge badge--sky" to={`/leads/${client.lead_id}`}>
              Към търговско досие →
            </Link>
          </div>
        </div>
      </header>

      <Charges clientId={clientId} />
      <Sites clientId={clientId} />
      <Tasks clientId={clientId} />

      {client.notes ? (
        <section className="cl__section">
          <div className="cl__section-head">
            <h3 className="cl__h">Оперативни бележки</h3>
          </div>
          <div className="cl__notes-box">
            <p className="cl__notes-text">{client.notes}</p>
          </div>
        </section>
      ) : null}
    </div>
  );
}
